import { useMemo, useEffect } from 'react';

import {
  Text,
  SimpleGrid,
  Box,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import DateInput from 'components/fields/home/DateInput';
import Divider from 'views/home_form/components/Divider';

import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';

import {
  changeDwellingAddressHistory,
  changeDwellingAddressHistoryItem,
  addDwellingAddressHistoryItem,
  removeDwellingAddressHistoryItem,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

interface AddressHistoryProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const AddressHistory = ({ dwelling, index }: AddressHistoryProps) => {
  const dispatch = useAppDispatch();
  const isDisabled = useIsBinderDisabled('homePersonalForm');

  // Check if address history is required (occupancy < 3 years)
  const isAddressHistoryRequired = useMemo(() => {
    const occupancyDate = dwelling.address_history?.occupancy_date;
    if (!occupancyDate) return false;

    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
    const occupancyDateObj = new Date(occupancyDate);

    return occupancyDateObj > threeYearsAgo;
  }, [dwelling.address_history?.occupancy_date]);

  const hasValidAddressHistory = useMemo(() => {
    const addressItems = dwelling.address_history?.items || [];
    return addressItems.some((item) => item.address);
  }, [dwelling.address_history?.items]);

  const showAddressRequiredWarning =
    isAddressHistoryRequired && !hasValidAddressHistory;

  // Auto-add address history item when required and none exists
  useEffect(() => {
    if (
      isAddressHistoryRequired &&
      (!dwelling.address_history?.items ||
        dwelling.address_history.items.length === 0)
    ) {
      dispatch(addDwellingAddressHistoryItem(index));
    }
  }, [
    isAddressHistoryRequired,
    dwelling.address_history?.items,
    dispatch,
    index,
  ]);

  const onOccupancyDateChange = (date: Date) => {
    dispatch(
      changeDwellingAddressHistory({
        dwellingIndex: index,
        name: 'occupancy_date',
        value: getDateString(date),
      }),
    );
  };

  const onAddressHistoryItemChange = (
    event: any,
    itemIndex: number,
    field: string,
  ) => {
    const { value } = event.target;

    dispatch(
      changeDwellingAddressHistoryItem({
        dwellingIndex: index,
        itemIndex,
        name: field,
        value,
      }),
    );
  };

  const onAddressHistoryItemDateChange = (
    date: Date,
    itemIndex: number,
    field: string,
  ) => {
    dispatch(
      changeDwellingAddressHistoryItem({
        dwellingIndex: index,
        itemIndex,
        name: field,
        value: getDateString(date),
      }),
    );
  };

  const handleAddAddressHistoryItem = () => {
    if (
      !dwelling.address_history?.items ||
      dwelling.address_history.items.length < 3
    ) {
      dispatch(addDwellingAddressHistoryItem(index));
    }
  };

  const handleRemoveAddressHistoryItem = (itemIndex: number) => {
    dispatch(
      removeDwellingAddressHistoryItem({ dwellingIndex: index, itemIndex }),
    );
  };

  return (
    <Box mt="20px">
      <Divider mb="15px">Address History</Divider>

      <Box mb="20px">
        <SimpleGrid columns={6} spacing="15px">
          <DateInput
            section={SECTIONS.DWELLINGS}
            label="Occupancy Date"
            date={getDateFromString(dwelling.address_history?.occupancy_date)}
            onChange={onOccupancyDateChange}
          />
        </SimpleGrid>
      </Box>

      {dwelling.address_history?.items &&
      dwelling.address_history.items.length > 0 ? (
        <>
          {dwelling.address_history.items.map((item, itemIndex: number) => (
            <Box
              key={itemIndex}
              mt={itemIndex === 0 ? '0' : '15px'}
              p="15px"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="15px"
              >
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Address {itemIndex + 1}
                </Text>
                {!(isAddressHistoryRequired && itemIndex === 0) && (
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleRemoveAddressHistoryItem(itemIndex)}
                  >
                    Remove
                  </Button>
                )}
              </Box>

              <SimpleGrid columns={6} spacing="15px" mb="15px">
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Address"
                  subLabel="Previous Address"
                  name={`address_history_address_${itemIndex}`}
                  value={item.address || ''}
                  onChange={(e: any) =>
                    onAddressHistoryItemChange(e, itemIndex, 'address')
                  }
                  required={isAddressHistoryRequired && itemIndex === 0}
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="City"
                  subLabel="Previous Address"
                  name={`address_history_city_${itemIndex}`}
                  value={item.city || ''}
                  onChange={(e: any) =>
                    onAddressHistoryItemChange(e, itemIndex, 'city')
                  }
                  required={isAddressHistoryRequired && itemIndex === 0}
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Province"
                  subLabel="Previous Address"
                  name={`address_history_province_${itemIndex}`}
                  value={item.province || ''}
                  onChange={(e: any) =>
                    onAddressHistoryItemChange(e, itemIndex, 'province')
                  }
                  required={isAddressHistoryRequired && itemIndex === 0}
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Postal Code"
                  subLabel="Previous Address"
                  name={`address_history_postal_code_${itemIndex}`}
                  value={item.postal_code || ''}
                  onChange={(e: any) =>
                    onAddressHistoryItemChange(e, itemIndex, 'postal_code')
                  }
                  required={isAddressHistoryRequired && itemIndex === 0}
                />
                <DateInput
                  section={SECTIONS.DWELLINGS}
                  label="Date Moved In"
                  date={getDateFromString(item.date_moved_in)}
                  onChange={(date: Date) =>
                    onAddressHistoryItemDateChange(
                      date,
                      itemIndex,
                      'date_moved_in',
                    )
                  }
                />
                <DateInput
                  section={SECTIONS.DWELLINGS}
                  label="Date Moved Out"
                  date={getDateFromString(item.date_moved_out)}
                  onChange={(date: Date) =>
                    onAddressHistoryItemDateChange(
                      date,
                      itemIndex,
                      'date_moved_out',
                    )
                  }
                />
              </SimpleGrid>
            </Box>
          ))}
        </>
      ) : (
        <Text fontSize="md" color="gray.600" fontStyle="italic">
          No address history added
        </Text>
      )}

      {showAddressRequiredWarning && (
        <Alert status="warning" mt="15px">
          <AlertIcon />
          <AlertDescription>
            The occupancy date indicates the client has lived at this address
            for less than 3 years. Please fill in the previous address details
            above.
          </AlertDescription>
        </Alert>
      )}

      {(!dwelling.address_history?.items ||
        dwelling.address_history.items.length < 3) && (
        <Box mt="15px">
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={handleAddAddressHistoryItem}
            disabled={isDisabled}
          >
            Add Address ({dwelling.address_history?.items?.length || 0}/3)
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AddressHistory;
